"use client";

import { c as _c } from "react/compiler-runtime";
import posthog from "posthog-js";
import { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { Drawer as MobileDrawer, DrawerContent as MobileDrawerContent, DrawerFooter as MobileDrawerFooter, DrawerHeader as MobileDrawerHeader, DrawerTitle as MobileDrawerTitle } from "@/components/shadcn/ui/drawer";
import { Button, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/ui";
import Input from "@/components/ui/input";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import { countAddon, remainingCapacity } from "@/components/visual-builder/service-addon-quantities";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useSelectionsDrawer } from "@/hooks/self-schedule/use-selections-drawer";
import { computeAddonMinutes } from "@/lib/service-utils";
import { cx } from "@/lib/tailwind-utils";
import { PublicServiceDetail } from "@/types/service";
import { getServicePricingForSelfSchedule } from "./helpers";
import { SelectionItemRow } from "./selection-item-row";
import { SelectionsSummary } from "./selections-summary";

// A few add-ons can be scanned at a glance; past that, search helps.
const SEARCH_THRESHOLD = 3;
interface SelectionsDrawerProps {
  service: PublicServiceDetail;
  existingAddonIds?: string[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (addonIds: string[]) => void;
  onRemove?: () => void;
}
export const SelectionsDrawer = t0 => {
  const $ = _c(132);
  const {
    service,
    existingAddonIds,
    isOpen,
    onOpenChange,
    onConfirm,
    onRemove
  } = t0;
  const {
    isMobile
  } = useIsMobile();
  const isEditing = !!existingAddonIds;
  let t1;
  if ($[0] !== existingAddonIds || $[1] !== service) {
    t1 = {
      service,
      initialAddonIds: existingAddonIds
    };
    $[0] = existingAddonIds;
    $[1] = service;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    draftAddonIds,
    toggleAddon,
    setAddonQuantity,
    isValid,
    validationMessage,
    selectedAddons,
    reset
  } = useSelectionsDrawer(t1);
  const [search, setSearch] = useState("");
  const showSearch = (service.addons?.length ?? 0) > SEARCH_THRESHOLD;
  let t2;
  if ($[3] !== search || $[4] !== service.addons) {
    bb0: {
      const query = search.trim().toLowerCase();
      if (!query) {
        let t3;
        if ($[6] !== service.addons) {
          t3 = service.addons ?? [];
          $[6] = service.addons;
          $[7] = t3;
        } else {
          t3 = $[7];
        }
        t2 = t3;
        break bb0;
      }
      t2 = (service.addons ?? []).filter(addon => addon.addonService.name.toLowerCase().includes(query));
    }
    $[3] = search;
    $[4] = service.addons;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const visibleAddons = t2;
  const selectionMax = service.selectionMax ?? null;
  const isRadioMode = selectionMax === 1;
  const maxReached = selectionMax !== null && draftAddonIds.length >= selectionMax;
  let t3;
  if ($[8] !== isOpen || $[9] !== reset || $[10] !== service.addons?.length || $[11] !== service.id || $[12] !== service.name) {
    t3 = () => {
      if (isOpen) {
        reset();
        setSearch("");
        posthog.capture("self_schedule_selections_opened", {
          service_id: service.id,
          service_name: service.name,
          num_addons: service.addons?.length ?? 0
        });
      }
    };
    $[8] = isOpen;
    $[9] = reset;
    $[10] = service.addons?.length;
    $[11] = service.id;
    $[12] = service.name;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== isOpen) {
    t4 = [isOpen];
    $[14] = isOpen;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  useEffect(t3, t4);
  let T0;
  let T1;
  let handleConfirm;
  let handleOpenChange;
  let renderHeader;
  let t10;
  let t11;
  let t12;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[16] !== draftAddonIds || $[17] !== existingAddonIds || $[18] !== isEditing || $[19] !== isMobile || $[20] !== isOpen || $[21] !== isRadioMode || $[22] !== maxReached || $[23] !== onConfirm || $[24] !== onOpenChange || $[25] !== search || $[26] !== selectionMax || $[27] !== service || $[28] !== setAddonQuantity || $[29] !== showSearch || $[30] !== toggleAddon || $[31] !== validationMessage || $[32] !== visibleAddons) {
    const handleToggle = addonId => {
      const addon_0 = service.addons?.find(a => a.id === addonId);
      const isAdding = countAddon(draftAddonIds, addonId) === 0;
      toggleAddon(addonId);
      posthog.capture("self_schedule_selection_toggled", {
        service_id: service.id,
        addon_id: addonId,
        addon_name: addon_0?.addonService.name,
        action: isAdding ? "add" : "remove"
      });
      if (selectionMax === 1 && isAdding && (service.selectionMin ?? 0) <= 1) {
        handleConfirm([addonId]);
      }
    };
    handleConfirm = t13 => {
      const addonIds = t13 === undefined ? draftAddonIds : t13;
      const confirmedAddons = addonIds.map(id => service.addons?.find(addon_1 => addon_1.id === id)).filter(_temp);
      if (isEditing) {
        posthog.capture("self_schedule_selections_edited", {
          service_id: service.id,
          prev_selections: existingAddonIds,
          new_selections: addonIds
        });
      } else {
        posthog.capture("self_schedule_selections_confirmed", {
          service_id: service.id,
          num_selections: addonIds.length,
          total_price_min: confirmedAddons.reduce(_temp2, 0),
          total_price_max: confirmedAddons.reduce(_temp3, 0),
          total_duration: computeAddonMinutes(service, confirmedAddons.map(_temp4))
        });
      }
      onConfirm(addonIds);
    };
    let t14;
    if ($[46] !== isOpen || $[47] !== onOpenChange || $[48] !== service.id) {
      t14 = open => {
        if (!open && isOpen) {
          posthog.capture("self_schedule_selections_dismissed", {
            service_id: service.id
          });
        }
        onOpenChange(open);
      };
      $[46] = isOpen;
      $[47] = onOpenChange;
      $[48] = service.id;
      $[49] = t14;
    } else {
      t14 = $[49];
    }
    handleOpenChange = t14;
    let t15;
    if ($[50] !== isEditing || $[51] !== service.name || $[52] !== validationMessage) {
      t15 = Title => <><Title>{service.name}</Title><p className="text-body text-foreground-secondary">{isEditing ? "Edit your selections" : "Customize your treatment"}</p>{validationMessage && <p className="text-caption text-foreground-muted mt-1">{validationMessage}</p>}</>;
      $[50] = isEditing;
      $[51] = service.name;
      $[52] = validationMessage;
      $[53] = t15;
    } else {
      t15 = $[53];
    }
    renderHeader = t15;
    t8 = "group";
    t9 = `Add-ons${validationMessage ? `, ${validationMessage}` : ""}`;
    const t16 = !isMobile && "flex min-h-0 flex-1 flex-col";
    if ($[54] !== t16) {
      t10 = cx(t16);
      $[54] = t16;
      $[55] = t10;
    } else {
      t10 = $[55];
    }
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <h3 className="text-caption-strong uppercase tracking-wide text-foreground-muted mb-2">Add-ons</h3>;
      $[56] = t11;
    } else {
      t11 = $[56];
    }
    if ($[57] !== search || $[58] !== showSearch) {
      t12 = showSearch && <Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search add-ons" aria-label="Search add-ons" icon={FiSearch} className="mb-2" />;
      $[57] = search;
      $[58] = showSearch;
      $[59] = t12;
    } else {
      t12 = $[59];
    }
    T1 = ScrollShadow.Root;
    const t17 = isMobile && "max-h-[50vh] flex-none";
    if ($[60] !== t17) {
      t7 = cx(t17);
      $[60] = t17;
      $[61] = t7;
    } else {
      t7 = $[61];
    }
    T0 = ScrollShadow.Viewport;
    if ($[62] !== search || $[63] !== visibleAddons.length) {
      t5 = visibleAddons.length === 0 && <p className="py-3 text-body text-foreground-muted">No add-ons match “{search.trim()}”</p>;
      $[62] = search;
      $[63] = visibleAddons.length;
      $[64] = t5;
    } else {
      t5 = $[64];
    }
    t6 = visibleAddons.map(addon_6 => {
      const count = countAddon(draftAddonIds, addon_6.id);
      const isSelected = count > 0;
      const isDisabled = maxReached && !isSelected;
      const maxForAddon = remainingCapacity(service, draftAddonIds, addon_6.id);
      return <SelectionItemRow key={addon_6.id} addon={addon_6} selected={isSelected} count={count} maxQuantity={Number.isFinite(maxForAddon) ? maxForAddon : undefined} disabled={isDisabled} mode={isRadioMode ? "radio" : "checkbox"} onToggle={() => handleToggle(addon_6.id)} onQuantityChange={addon_6.addonService.allowMultipleBookings === true ? quantity => setAddonQuantity(addon_6.id, quantity) : undefined} className="shadow-sm rounded-lg" />;
    });
    $[16] = draftAddonIds;
    $[17] = existingAddonIds;
    $[18] = isEditing;
    $[19] = isMobile;
    $[20] = isOpen;
    $[21] = isRadioMode;
    $[22] = maxReached;
    $[23] = onConfirm;
    $[24] = onOpenChange;
    $[25] = search;
    $[26] = selectionMax;
    $[27] = service;
    $[28] = setAddonQuantity;
    $[29] = showSearch;
    $[30] = toggleAddon;
    $[31] = validationMessage;
    $[32] = visibleAddons;
    $[33] = T0;
    $[34] = T1;
    $[35] = handleConfirm;
    $[36] = handleOpenChange;
    $[37] = renderHeader;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
  } else {
    T0 = $[33];
    T1 = $[34];
    handleConfirm = $[35];
    handleOpenChange = $[36];
    renderHeader = $[37];
    t10 = $[38];
    t11 = $[39];
    t12 = $[40];
    t5 = $[41];
    t6 = $[42];
    t7 = $[43];
    t8 = $[44];
    t9 = $[45];
  }
  let t13;
  if ($[65] !== T0 || $[66] !== t5 || $[67] !== t6) {
    t13 = <T0>{t5}{t6}</T0>;
    $[65] = T0;
    $[66] = t5;
    $[67] = t6;
    $[68] = t13;
  } else {
    t13 = $[68];
  }
  let t14;
  let t15;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ScrollShadow.Top />;
    t15 = <ScrollShadow.Bottom />;
    $[69] = t14;
    $[70] = t15;
  } else {
    t14 = $[69];
    t15 = $[70];
  }
  let t16;
  if ($[71] !== T1 || $[72] !== t13 || $[73] !== t7) {
    t16 = <T1 className={t7}>{t13}{t14}{t15}</T1>;
    $[71] = T1;
    $[72] = t13;
    $[73] = t7;
    $[74] = t16;
  } else {
    t16 = $[74];
  }
  let t17;
  if ($[75] !== t10 || $[76] !== t11 || $[77] !== t12 || $[78] !== t16 || $[79] !== t8 || $[80] !== t9) {
    t17 = <div role={t8} aria-label={t9} className={t10}>{t11}{t12}{t16}</div>;
    $[75] = t10;
    $[76] = t11;
    $[77] = t12;
    $[78] = t16;
    $[79] = t8;
    $[80] = t9;
    $[81] = t17;
  } else {
    t17 = $[81];
  }
  const addonList = t17;
  let t18;
  if ($[82] !== service) {
    t18 = getServicePricingForSelfSchedule(service);
    $[82] = service;
    $[83] = t18;
  } else {
    t18 = $[83];
  }
  const pricing_0 = t18;
  const t19 = pricing_0.displayPrice ?? 0;
  let t20;
  if ($[84] !== pricing_0.displayPriceMax || $[85] !== pricing_0.estimated || $[86] !== pricing_0.priceStartsAt || $[87] !== selectedAddons || $[88] !== service || $[89] !== t19) {
    t20 = <div className="max-h-48 flex-shrink-0 pt-4 border-t border-border"><SelectionsSummary basePrice={t19} basePriceMax={pricing_0.displayPriceMax} baseBillingPrice={service.price} displayPriceIncludesAddons={service.displayPriceIncludesAddons} baseDuration={service.duration} baseService={service} selectedAddons={selectedAddons} estimated={pricing_0.estimated} priceStartsAt={pricing_0.priceStartsAt} /></div>;
    $[84] = pricing_0.displayPriceMax;
    $[85] = pricing_0.estimated;
    $[86] = pricing_0.priceStartsAt;
    $[87] = selectedAddons;
    $[88] = service;
    $[89] = t19;
    $[90] = t20;
  } else {
    t20 = $[90];
  }
  const summary = t20;
  let t21;
  if ($[91] !== onRemove) {
    t21 = onRemove && <Button variant="secondary" onClick={onRemove} className="flex-1 text-nowrap">Remove Service</Button>;
    $[91] = onRemove;
    $[92] = t21;
  } else {
    t21 = $[92];
  }
  let t22;
  if ($[93] !== handleConfirm) {
    t22 = () => handleConfirm();
    $[93] = handleConfirm;
    $[94] = t22;
  } else {
    t22 = $[94];
  }
  const t23 = !isValid;
  const t24 = isEditing ? "Update" : "Continue";
  let t25;
  if ($[95] !== t22 || $[96] !== t23 || $[97] !== t24) {
    t25 = <Button onClick={t22} variant="primary" disabled={t23} className="flex-1 min-h-12">{t24}</Button>;
    $[95] = t22;
    $[96] = t23;
    $[97] = t24;
    $[98] = t25;
  } else {
    t25 = $[98];
  }
  let t26;
  if ($[99] !== t21 || $[100] !== t25) {
    t26 = <div className="flex gap-2">{t21}{t25}</div>;
    $[99] = t21;
    $[100] = t25;
    $[101] = t26;
  } else {
    t26 = $[101];
  }
  const footerActions = t26;
  if (isMobile) {
    let t27;
    if ($[102] !== renderHeader) {
      t27 = <MobileDrawerHeader className="pb-1">{renderHeader(MobileDrawerTitle)}</MobileDrawerHeader>;
      $[102] = renderHeader;
      $[103] = t27;
    } else {
      t27 = $[103];
    }
    let t28;
    if ($[104] !== addonList || $[105] !== summary) {
      t28 = <div className="flex flex-col justify-between overflow-y-auto px-4">{addonList}{summary}</div>;
      $[104] = addonList;
      $[105] = summary;
      $[106] = t28;
    } else {
      t28 = $[106];
    }
    let t29;
    if ($[107] !== footerActions) {
      t29 = <MobileDrawerFooter className="space-y-3 pt-3">{footerActions}</MobileDrawerFooter>;
      $[107] = footerActions;
      $[108] = t29;
    } else {
      t29 = $[108];
    }
    let t30;
    if ($[109] !== t27 || $[110] !== t28 || $[111] !== t29) {
      t30 = <MobileDrawerContent className="max-h-[90vh] bg-background/90">{t27}{t28}{t29}</MobileDrawerContent>;
      $[109] = t27;
      $[110] = t28;
      $[111] = t29;
      $[112] = t30;
    } else {
      t30 = $[112];
    }
    let t31;
    if ($[113] !== handleOpenChange || $[114] !== isOpen || $[115] !== t30) {
      t31 = <MobileDrawer open={isOpen} onOpenChange={handleOpenChange}>{t30}</MobileDrawer>;
      $[113] = handleOpenChange;
      $[114] = isOpen;
      $[115] = t30;
      $[116] = t31;
    } else {
      t31 = $[116];
    }
    return t31;
  }
  let t27;
  if ($[117] !== renderHeader) {
    t27 = <DrawerHeader className="pb-1">{renderHeader(DrawerTitle)}</DrawerHeader>;
    $[117] = renderHeader;
    $[118] = t27;
  } else {
    t27 = $[118];
  }
  let t28;
  if ($[119] !== addonList || $[120] !== summary) {
    t28 = <DrawerBody className="h-full flex flex-col justify-between">{addonList}{summary}</DrawerBody>;
    $[119] = addonList;
    $[120] = summary;
    $[121] = t28;
  } else {
    t28 = $[121];
  }
  let t29;
  if ($[122] !== footerActions) {
    t29 = <DrawerFooter className="space-y-3 pt-3">{footerActions}</DrawerFooter>;
    $[122] = footerActions;
    $[123] = t29;
  } else {
    t29 = $[123];
  }
  let t30;
  if ($[124] !== t27 || $[125] !== t28 || $[126] !== t29) {
    t30 = <DrawerContent side="right" className="bg-background/90">{t27}{t28}{t29}</DrawerContent>;
    $[124] = t27;
    $[125] = t28;
    $[126] = t29;
    $[127] = t30;
  } else {
    t30 = $[127];
  }
  let t31;
  if ($[128] !== handleOpenChange || $[129] !== isOpen || $[130] !== t30) {
    t31 = <Drawer open={isOpen} onOpenChange={handleOpenChange}>{t30}</Drawer>;
    $[128] = handleOpenChange;
    $[129] = isOpen;
    $[130] = t30;
    $[131] = t31;
  } else {
    t31 = $[131];
  }
  return t31;
};
function _temp(addon_2) {
  return addon_2 !== undefined;
}
function _temp2(sum, addon_3) {
  return sum + getServicePricingForSelfSchedule(addon_3.addonService).displayPrice;
}
function _temp3(sum_0, addon_4) {
  const pricing = getServicePricingForSelfSchedule(addon_4.addonService);
  return sum_0 + Math.max(pricing.displayPriceMax ?? 0, pricing.displayPrice);
}
function _temp4(addon_5) {
  return addon_5.addonService.duration;
}